/**
 * Lu Moments product page.
 *
 * One module for the whole buying panel: the phone photo strip counter, the live inscription
 * preview (Type A), the delivery calendar, the ZIP check, quantity, gift message, validation, the
 * proof-read step, add-to-bag (posting to Shopify's cart API and telling Horizon's cart drawer),
 * and the phone bottom bar. Configuration arrives as JSON from the section; nothing is hard-coded.
 */

function init(root) {
  const cfg = JSON.parse(root.querySelector('[data-lm-config]').textContent);
  const $ = (sel, scope = root) => scope.querySelector(sel);
  const form = $('[data-lm-form]');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const cta = $('[data-cta]');
  const missingEl = $('[data-missing]');
  const nameInput = $('[data-name]');
  const nameInput2 = $('[data-name2]');
  const msgSelect = $('[data-msg-select]');
  const customWrap = $('[data-custom-wrap]');
  const customInput = $('[data-custom]');
  const customCount = $('[data-custom-count]');
  const propMessage = $('[data-prop-message]');
  const personalise = Boolean(cfg.personalise && nameInput && msgSelect);

  /* Calendar and ZIP first: the personalisation paint below reads their state. */
  const calendar = createCalendar(root, cfg, () => updateState());
  const zip = createZipCheck(root, cfg, () => updateState());

  /* ------------------------------------------------------------------ photo strip counter */
  const strip = $('[data-lm-strip]');
  const counter = $('[data-lm-counter]');
  if (strip && counter) {
    const slides = [...strip.children];
    counter.textContent = `1 | ${slides.length}`;
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
            counter.textContent = `${slides.indexOf(entry.target) + 1} | ${slides.length}`;
          }
        }
      },
      { root: strip, threshold: [0.6] }
    );
    slides.forEach((s) => io.observe(s));

    // Reel arrows (complete-collection pages): step one slide at a time.
    const step = (dir) => {
      const x = strip.scrollLeft;
      const next = dir > 0 ? slides.find((s) => s.offsetLeft > x + 4) : [...slides].reverse().find((s) => s.offsetLeft < x - 4);
      if (next) glide(strip, Math.min(next.offsetLeft, strip.scrollWidth - strip.clientWidth), 550);
    };
    root.querySelector('[data-reel-prev]')?.addEventListener('click', () => step(-1));
    root.querySelector('[data-reel-next]')?.addEventListener('click', () => step(1));
  }

  /* ------------------------------------------------------------------ personalisation */
  let preview = null;

  // Declared up here because paint() reads it, and paint() runs long before the logo-or-text
  // block below: a `let` down there leaves it in the temporal dead zone and paint() throws.
  let markMode = 'text';
  const cameoMode = () => root.querySelector('[data-cameo-mode]:checked')?.value || 'A number';
  const ageWrap = $('[data-age-wrap]');
  const ageInput = $('[data-age]');
  const templated = Boolean(cfg.spec?.phrases);

  if (personalise) {
    // A layout with no blank plate yet: the wording is still chosen and sent with the order,
    // the balloon picture simply is not redrawn.
    if (cfg.spec?.noPreview) {
      const slide = root.querySelector('[data-lm-preview-slide]');
      if (slide) slide.remove();
    }
    if (cfg.spec?.layout === 'cameo') {
      // The frame always takes something; the number is optional, so nothing is required here.
      const cf = $('[data-cameo-fields]');
      if (cf) cf.hidden = false;
      if (ageWrap) ageWrap.hidden = false;
      const lab = $('[data-age-label]');
      if (lab) lab.textContent = 'The number';
      const hint = $('[data-age-hint]');
      if (hint) hint.textContent = 'Set in gold inside the frame. Leave it blank for a celebration that is not a birthday.';
      if (ageInput) ageInput.placeholder = 'Leave blank for no number';
    } else if (cfg.spec?.ageOnly) {
      // Only a number is chosen here, so there is nothing to pick from a list.
      if (ageWrap) ageWrap.hidden = false;
      const lab = $('[data-age-label]');
      if (lab) lab.textContent = 'The age';
      const hint = $('[data-age-hint]');
      if (hint) hint.textContent = 'The birthday age, set on the balloon in the collection\u2019s script.';
    } else if (templated) {
      buildPhraseSelect(msgSelect, cfg.spec.phrases);
      const lbl = $('[data-msg-label]');
      if (lbl) lbl.textContent = 'Choose your line';
      customInput.placeholder =
        cfg.spec.layout === 'badge'
          ? 'Up to two short words per line'
          : cfg.spec.layout === 'shell' || cfg.spec.layout === 'tail'
          ? 'Your own message'
          : cfg.spec.layout === 'seahorse'
          ? 'Your own wording, beneath the arc'
          : 'Your own second line';
    } else {
      buildMessageSelect(msgSelect, cfg.messages, cfg.spec?.msg);
    }
    if (cfg.spec?.fixedType) {
      const tf = $('[data-typo-fields]');
      if (tf) tf.hidden = true;
    }
    preview = createPreview(root, cfg);

    const paint = () => {
      const isCustom = msgSelect.value === '__custom';
      customWrap.hidden = !isCustom;
      let message = cfg.spec?.nameOnly ? '' : isCustom ? customInput.value.trim() : msgSelect.value;
      let template = null;
      if (cfg.spec?.layout === 'pigskin') {
        // The ball carries its own line-item property, so there is no message line to compose.
        message = '';
      } else if (cfg.spec?.layout === 'cameo') {
        const portrait = cameoMode() === 'Cinderella';
        if (ageWrap) ageWrap.hidden = portrait;
        if (ageInput) ageInput.disabled = portrait;
        message = portrait ? 'Cinderella in the frame' : ageInput?.value.trim() || '';
      } else if (cfg.spec?.ageOnly) {
        message = ageInput?.value.trim() || '';
      } else if (templated) {
        template = isCustom ? customTemplate(cfg.spec, customInput.value.trim()) : parseTemplate(msgSelect.value);
        const needsAge = template.some((seg) => seg.includes('{age}')) && !cfg.spec?.noPreview;
        if (ageWrap) ageWrap.hidden = !needsAge;
        // what the order receives: the finished line, tokens filled in
        message = fillTemplate(template, nameInput.value.trim(), ageInput?.value.trim() || '').filter(Boolean).join(' · ');
      }
      propMessage.value = message;
      customCount.textContent = `${customInput.value.length} / 40`;
      customCount.classList.toggle('is-over', customInput.value.length >= 40);
      preview?.paint({
        name: nameInput.value.trim(),
        name2: nameInput2?.value.trim() || '',
        message,
        style: currentStyle(),
        template,
        age: ageInput?.value.trim() || '',
        // carried through so a repaint from another field does not blank the ball
        logoMode: markMode === 'logo',
        custom: $('[data-mark-input]')?.value.trim() || '',
        cameoMode: cameoMode(),
      });
      updateState();
    };
    root.querySelectorAll('[data-cameo-mode]').forEach((r) => r.addEventListener('change', paint));
    nameInput.addEventListener('input', paint);
    nameInput2?.addEventListener('input', paint);
    customInput.addEventListener('input', paint);
    ageInput?.addEventListener('input', () => {
      ageInput.value = ageInput.value.replace(/[^0-9a-zA-Z#]/g, '').slice(0, 3);
      paint();
    });
    msgSelect.addEventListener('change', () => {
      paint();
      if (msgSelect.value === '__custom') customInput.focus();
    });
    root.querySelectorAll('[data-typo]').forEach((r) => r.addEventListener('change', paint));
    // Never let the browser restore a previously typed name.
    nameInput.value = '';
    if (nameInput2) nameInput2.value = '';
    customInput.value = '';

    // The preview is the last photograph. The moment the customer starts personalising it comes
    // into view by itself: on phones the strip slides across to it; on tablets and desktops the
    // page scrolls it up beside the (sticky) panel. Only when it isn't already mostly visible, so
    // it never fights the customer's own scrolling.
    const previewSlide = root.querySelector('[data-lm-preview-slide]');
    const personaliseBlock = root.querySelector('.lm-personalise');
    if (strip && previewSlide && personaliseBlock) {
      const phone = window.matchMedia('(max-width: 767px)');
      const behavior = reducedMotion ? 'auto' : 'smooth';
      const show = () => {
        const horizontal = phone.matches || strip.scrollWidth > strip.clientWidth + 4;
        if (horizontal) {
          // The preview is the last slide: on a wide reel it can't reach the left edge, so land
          // it against the right edge (the reel's end) where it is fully in view.
          const target = Math.min(previewSlide.offsetLeft, strip.scrollWidth - strip.clientWidth);
          if (Math.abs(strip.scrollLeft - target) > 4) glide(strip, target, reducedMotion ? 0 : 700);
          previewSlide.classList.add('is-attention');
          setTimeout(() => previewSlide.classList.remove('is-attention'), 1600);
          if (phone.matches) return;
        }
        const r = (horizontal ? strip : previewSlide).getBoundingClientRect();
        const header = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--lm-header-height'), 10) || 72;
        const visible = Math.min(r.bottom, window.innerHeight) - Math.max(r.top, header);
        if (visible < r.height * 0.6) (horizontal ? strip : previewSlide).scrollIntoView({ block: 'start', behavior });
      };
      personaliseBlock.addEventListener('focusin', show);
      personaliseBlock.addEventListener('pointerdown', show);
      let typed = false;
      personaliseBlock.addEventListener('input', () => {
        if (!typed) show();
        typed = true;
      });
      personaliseBlock.addEventListener('change', show);
    }
    if (document.fonts?.ready) document.fonts.ready.then(paint);
    paint();
  }

  function currentStyle() {
    return root.querySelector('[data-typo]:checked')?.dataset.typo || 'house';
  }

  /* ------------------------------------------------------------------ options (variants) */
  const variantsEl = $('[data-lm-variants]');
  if (variantsEl) {
    const variants = JSON.parse(variantsEl.textContent);
    const idInput = form.querySelector('input[name="id"]');
    const priceEl = $('[data-lm-price]');
    const ctaPrice = $('[data-cta-price]');
    const barBtn = $('[data-bar-btn]');
    const pickVariant = () => {
      const chosen = [...root.querySelectorAll('[data-option]:checked')].map((r) => r.value);
      const v = variants.find((x) => chosen.every((val, i) => x.options[i] === val));
      if (!v) return;
      idInput.value = String(v.id);
      if (priceEl) priceEl.textContent = v.price;
      if (ctaPrice) ctaPrice.textContent = v.price;
      if (barBtn) barBtn.innerHTML = `Add to bag <span class="lm-bar__sep">—</span> ${v.price}`;
    };
    root.querySelectorAll('[data-option]').forEach((r) => r.addEventListener('change', pickVariant));
    pickVariant();
  }

  /* ------------------------------------------------------------------ quantity */
  const qty = $('[data-qty]');
  const clampQty = () => {
    const v = Math.min(Number(qty.max) || 5, Math.max(1, parseInt(qty.value, 10) || 1));
    qty.value = String(v);
  };
  $('[data-qty-minus]')?.addEventListener('click', () => {
    qty.value = String((parseInt(qty.value, 10) || 1) - 1);
    clampQty();
  });
  $('[data-qty-plus]')?.addEventListener('click', () => {
    qty.value = String((parseInt(qty.value, 10) || 1) + 1);
    clampQty();
  });
  qty?.addEventListener('change', clampQty);

  /* ------------------------------------------------------------------ gift message */
  const giftToggle = $('[data-gift-toggle]');
  const giftWrap = $('[data-gift-wrap]');
  const gift = $('[data-gift]');
  const giftTo = $('[data-gift-to]');
  const giftFrom = $('[data-gift-from]');
  const giftCount = $('[data-gift-count]');
  const giftMax = Number(gift?.maxLength) || 60;
  giftToggle?.addEventListener('click', () => {
    const open = giftWrap.hidden;
    giftWrap.hidden = !open;
    giftToggle.setAttribute('aria-expanded', String(open));
    giftToggle.textContent = open ? 'Remove gift tag message' : 'Add a gift tag message';
    if (open) giftTo.focus();
    else [gift, giftTo, giftFrom].forEach((el) => (el.value = ''));
    giftCount.textContent = `${gift.value.length} / ${giftMax}`;
  });
  gift?.addEventListener('input', () => {
    giftCount.textContent = `${gift.value.length} / ${giftMax}`;
    giftCount.classList.toggle('is-over', gift.value.length >= giftMax);
  });

  /* ------------------------------------------------------------------ logo or text */
  // One piece lets the customer either upload a logo or write a short line. Only the chosen
  // one is sent with the order, so the atelier never receives both.
  const markFields = $('[data-mark-fields]');
  if (markFields && cfg.spec?.layout === 'pigskin') {
    markFields.hidden = false;
    const textWrap = $('[data-mark-text]');
    const fileWrap = $('[data-mark-file]');
    const textInput = $('[data-mark-input]');
    const fileInput = $('[data-mark-upload]');
    const count = $('[data-mark-count]');
    const sync = () => {
      markMode = root.querySelector('[data-mark-mode]:checked')?.value || 'text';
      const logo = markMode === 'logo';
      textWrap.hidden = logo;
      fileWrap.hidden = !logo;
      textInput.disabled = logo;
      fileInput.disabled = !logo;
      count.textContent = `${textInput.value.length} / 20`;
      preview?.paint({ ...lastMark(), logoMode: logo, custom: textInput.value.trim() });
      updateState();
    };
    const lastMark = () => ({ name: nameInput.value.trim(), message: propMessage.value, style: currentStyle() });
    root.querySelectorAll('[data-mark-mode]').forEach((r) => r.addEventListener('change', sync));
    textInput.addEventListener('input', sync);
    fileInput.addEventListener('change', sync);
    sync();
  }

  /* ------------------------------------------------------------------ validation */
  function missing() {
    const list = [];
    if (personalise) {
      if (cfg.spec?.layout === 'pigskin') {
        const textInput = $('[data-mark-input]');
        const fileInput = $('[data-mark-upload]');
        if (markMode === 'logo' && !fileInput?.files?.length) list.push({ text: 'Choose a logo file', el: fileInput });
        if (markMode === 'text' && !textInput?.value.trim()) list.push({ text: 'Write the line for the ball', el: textInput });
      } else if (cfg.spec?.ageOnly) {
        if (!ageInput?.value.trim()) list.push({ text: 'Enter the age', el: ageInput });
      } else if (cfg.spec?.cameoOnly) {
        // Both choices are complete on their own, and a blank number is a deliberate option.
      } else {
        if (!cfg.spec?.messageOnly && !cfg.spec?.markOnly && !nameInput.value.trim()) list.push({ text: 'Enter a name', el: nameInput });
        if (cfg.spec?.name2 && nameInput2 && !nameInput2.value.trim()) list.push({ text: 'Enter the second name', el: nameInput2 });
        if (!cfg.spec?.nameOnly && !cfg.spec?.markOnly && !propMessage.value) list.push({ text: 'Choose or write a message', el: msgSelect.value === '__custom' ? customInput : msgSelect });
      }
      if (templated && !cfg.spec?.ageOnly && ageWrap && !ageWrap.hidden && !ageInput.value.trim()) list.push({ text: 'Enter the number for the badge', el: ageInput });
    }
    if (!calendar.value) list.push({ text: 'Choose a delivery date', el: calendar.toggle });
    if (!zip.ok) list.push({ text: 'Check your ZIP code', el: zip.input });
    return list;
  }

  function updateState() {
    const incomplete = missing().length > 0;
    cta.setAttribute('aria-disabled', String(incomplete));
    if (!incomplete) missingEl.hidden = true;
  }

  /* ------------------------------------------------------------------ proof-read + submit */
  const proof = $('[data-proof]');
  let proofed = !personalise;

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const gaps = missing();
    if (gaps.length) {
      missingEl.textContent = gaps.map((g) => g.text).join(' · ');
      missingEl.hidden = false;
      const first = gaps[0].el;
      if (first === calendar.toggle) calendar.open();
      first.focus({ preventScroll: false });
      first.scrollIntoView?.({ block: 'center', behavior: reducedMotion ? 'auto' : 'smooth' });
      return;
    }
    if (personalise && !proofed) {
      $('[data-proof-name]').textContent = nameInput.value.trim();
      $('[data-proof-name]').parentElement.hidden = !nameInput.value.trim();
      $('[data-proof-msg]').textContent = propMessage.value;
      $('[data-proof-msg]').parentElement.hidden = !propMessage.value;
      $('[data-proof-typo]').parentElement.hidden = !propMessage.value;
      $('[data-proof-typo]').textContent = root.querySelector('[data-typo]:checked')?.value || '';
      proof.hidden = false;
      proof.scrollIntoView({ block: 'nearest', behavior: reducedMotion ? 'auto' : 'smooth' });
      $('[data-proof-ok]').focus();
      return;
    }
    addToBag();
  });

  $('[data-proof-edit]')?.addEventListener('click', () => {
    proof.hidden = true;
    nameInput.focus();
  });
  $('[data-proof-ok]')?.addEventListener('click', () => {
    proofed = true;
    proof.hidden = true;
    addToBag();
  });

  async function addToBag() {
    const label = $('[data-cta-label]');
    cta.classList.add('is-busy');
    label.textContent = 'Adding…';

    const formData = new FormData(form);
    const cartItems = document.querySelectorAll('cart-items-component');
    const sectionIds = [...cartItems].map((el) => el.dataset.sectionId).filter(Boolean);
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));

    // Tell Horizon's header and cart drawer, the same way its own product form does.
    let events = null;
    try {
      events = await import('@shopify/events');
    } catch (_) {
      events = null;
    }
    const deferred = events?.CartLinesUpdateEvent?.createPromise?.();
    if (events && deferred) {
      form.dispatchEvent(
        new events.CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: String(formData.get('id')), quantity: Number(formData.get('quantity')) || 1 }],
          promise: deferred.promise,
        })
      );
    }

    try {
      const res = await fetch(cfg.cartAddUrl, {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
        body: formData,
      });
      const data = await res.json();
      if (data.status) throw new Error(data.description || data.message || 'Could not add to bag');

      const cartRes = await fetch(`${cfg.cartUrl}.js`, { headers: { Accept: 'application/json' } });
      const cart = await cartRes.json();
      deferred?.resolve({
        cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'lm-product',
          sourceId: form.id,
          itemCount: Number(formData.get('quantity')) || 1,
          productId: String(cfg.productId),
          sections: data.sections,
          didError: false,
        },
      });

      label.textContent = 'Added to bag';
      setTimeout(() => (label.textContent = 'Add to bag'), 2400);
      proofed = !personalise;

      // Show the bag. Horizon's drawer only opens itself when its auto-open setting is on, so open
      // it here once its contents have re-rendered; without a drawer, go to the cart page.
      const drawerComponent = document.querySelector('cart-drawer-component');
      const themeDrawer = drawerComponent?.closest('theme-drawer');
      if (themeDrawer) {
        setTimeout(() => {
          if (!themeDrawer.hasAttribute('open')) themeDrawer.open?.();
        }, 350);
      } else {
        window.location.href = cfg.cartUrl;
      }
    } catch (err) {
      deferred?.reject(err);
      missingEl.textContent = err.message || 'Something went wrong. Please try again.';
      missingEl.hidden = false;
      label.textContent = 'Add to bag';
    } finally {
      cta.classList.remove('is-busy');
    }
  }

  /* ------------------------------------------------------------------ phone bottom bar */
  const bar = $('[data-lm-bar]');
  if (bar && cta) {
    bar.hidden = false;
    const io = new IntersectionObserver(
      ([entry]) => {
        const panelAbove = entry.boundingClientRect.top < 0;
        const ctaBelow = entry.boundingClientRect.top > window.innerHeight;
        bar.classList.toggle('is-visible', !entry.isIntersecting && (panelAbove || ctaBelow));
      },
      { threshold: 0 }
    );
    io.observe(cta);
    $('[data-bar-btn]').addEventListener('click', () => {
      cta.scrollIntoView({ block: 'center', behavior: reducedMotion ? 'auto' : 'smooth' });
      setTimeout(() => cta.focus({ preventScroll: true }), 400);
    });
  }

  updateState();
}

/* ==================================================================== Horizontal glide */
/**
 * Scroll a snap strip to `left` over `ms`. Browsers drop a smooth scrollTo() inside a
 * scroll-snap container, so this drives it by hand with snapping paused, and always lands on
 * the exact position even if animation frames are throttled.
 */
function glide(el, left, ms) {
  const start = el.scrollLeft;
  const delta = left - start;
  if (!ms || Math.abs(delta) < 1) {
    el.scrollLeft = left;
    return;
  }
  const snap = el.style.scrollSnapType;
  el.style.scrollSnapType = 'none';
  const t0 = performance.now();
  let done = false;
  const finish = () => {
    if (done) return;
    done = true;
    el.scrollLeft = left;
    el.style.scrollSnapType = snap;
  };
  const tick = (now) => {
    const p = Math.min(1, (now - t0) / ms);
    const e = 1 - Math.pow(1 - p, 3);
    el.scrollLeft = start + delta * e;
    if (p < 1) requestAnimationFrame(tick);
    else finish();
  };
  requestAnimationFrame(tick);
  setTimeout(finish, ms + 80);
}

/**
 * A line of real text curved along a circular arc, drawn in SVG so it scales with the plate.
 * The arc's centre, radius and font size are fractions of the bubble, measured from the print.
 */
function setArc(svg, text, A, bw, bh) {
  const cx = A.cx * bw;
  const cy = A.cy * bh;
  const r = A.r * bw;
  const F = A.F * bw;
  const pad = F * 2.2;
  const size = (r + pad) * 2;
  Object.assign(svg.style, {
    left: `${cx - r - pad}px`,
    top: `${cy - r - pad}px`,
    width: `${size}px`,
    height: `${size}px`,
    color: A.colour,
    overflow: 'visible',
    // the printed line is not always level: the art sits on a sphere, so a long arc leans
    transform: A.rot ? `rotate(${A.rot}deg)` : '',
  });
  svg.setAttribute('viewBox', `0 0 ${size} ${size}`);
  const c = size / 2;
  // the baseline circle: the cap-line arc measured from the print, dropped by the font's baseline
  const br = r + A.baseOff * F;
  const path = svg.querySelector('path');
  // A whole circle, starting at the bottom and running clockwise, so the halfway point is the
  // top. A semicircle caps the text at 180 degrees, and some lines wrap further than that.
  path.setAttribute('d', `M ${c} ${c + br} A ${br} ${br} 0 1 1 ${c} ${c - br} A ${br} ${br} 0 1 1 ${c} ${c + br}`);
  const tp = svg.querySelector('textPath');
  tp.textContent = text;
  const t = svg.querySelector('text');
  t.setAttribute('font-family', A.font);
  t.setAttribute('font-weight', String(A.weight || 400));
  t.setAttribute('font-size', `${F}`);
}

/* ==================================================================== Phrase templates */
/**
 * A phrase is "segment | segment | segment": the layout decides what each segment is (a line, the
 * badge number). {name} and {age} are filled from the fields. In the dropdown, tokens read as
 * [Name] and [Number].
 */
function parseTemplate(str) {
  return String(str || '')
    .split('|')
    .map((s) => s.trim());
}
function fillTemplate(segs, name, age) {
  return segs.map((s) => s.replace(/\{name\}/g, name || '').replace(/\{age\}/g, age || '').replace(/\s+/g, ' ').trim());
}
function customTemplate(spec, text) {
  // The tail and the shell carry the words on their own, with nothing above them.
  if (spec.layout === 'tail' || spec.layout === 'shell' || spec.layout === 'mirror') return [text];
  // The seahorse keeps its arc and takes your words on the lines beneath it.
  if (spec.layout === 'seahorse') return [spec.customLine1 || '', text];
  if (spec.layout === 'badge') {
    // two short lines, split at the word gap nearest the middle
    const words = text.split(/\s+/).filter(Boolean);
    if (words.length < 2) return [text, ''];
    let best = 1;
    let bestD = Infinity;
    for (let i = 1; i < words.length; i++) {
      const d = Math.abs(words.slice(0, i).join(' ').length - words.slice(i).join(' ').length);
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    }
    return [words.slice(0, best).join(' '), words.slice(best).join(' ')];
  }
  // `customLine1` may carry several fixed lines separated by |, so a piece whose house wording
  // runs over three lines still lands the customer's own words on the line that varies.
  return [...parseTemplate(spec.customLine1 || ''), text, '{age}'];
}
function buildPhraseSelect(select, phrases) {
  select.innerHTML = '';
  for (const p of phrases) {
    const opt = document.createElement('option');
    opt.value = p;
    opt.textContent = parseTemplate(p)
      .filter(Boolean)
      .map((s) => s.replace(/\{name\}/g, '[Name]').replace(/\{age\}/g, '[Number]'))
      .join(' · ');
    select.appendChild(opt);
  }
  const own = document.createElement('optgroup');
  own.label = 'Your own words';
  const custom = document.createElement('option');
  custom.value = '__custom';
  custom.textContent = 'Write my own…';
  own.appendChild(custom);
  select.appendChild(own);
}

/* ==================================================================== House Messages */
function buildMessageSelect(select, raw, preferred) {
  select.innerHTML = '';
  let group = null;
  const lines = String(raw || '').split(/\r?\n/);
  for (const line of lines) {
    const t = line.trim();
    if (!t) continue;
    if (t.startsWith('#')) {
      group = document.createElement('optgroup');
      group.label = t.replace(/^#\s*/, '');
      select.appendChild(group);
      continue;
    }
    const opt = document.createElement('option');
    opt.textContent = t;
    opt.value = t;
    (group || select).appendChild(opt);
  }
  const own = document.createElement('optgroup');
  own.label = 'Your own words';
  const custom = document.createElement('option');
  custom.value = '__custom';
  custom.textContent = 'Write my own…';
  own.appendChild(custom);
  select.appendChild(own);
  if (preferred && [...select.options].some((o) => o.value === preferred)) select.value = preferred;
}

/* ==================================================================== Live inscription preview */
/**
 * Everything is measured against the bubble, not the photograph. `spec.geom` is the bubble's box
 * as % of the plate; sizes and x-positions are fractions of the bubble width (bw), baselines and
 * y-positions fractions of the bubble height (bh). Text is placed by baseline: top = baseline − k·F,
 * where k is each font's baseline offset at line-height 1.
 */
const BASE = { script: 0.6, house: 0.795, bodoni: 0.855, serif: 0.82, minimal: 0.845, cirque: 0.78 };

function probeWidth(text, fontFamily, host) {
  const s = document.createElement('span');
  s.style.cssText = `position:absolute;visibility:hidden;white-space:nowrap;font:100px ${fontFamily}`;
  s.textContent = text;
  host.appendChild(s);
  const w = s.getBoundingClientRect().width;
  s.remove();
  return w;
}
const FAM = { house: 'f-house', script: 'f-script', serif: 'f-serif', minimal: 'f-minimal' };
const ASPECT = { oh: 0.411, crest: 1.018, teddy: 0.352 };

function createPreview(root, cfg) {
  const spec = cfg.spec;
  const box = root.querySelector('[data-lm-preview]');
  if (!spec || !box) return null;
  const plate = box.querySelector('.lm-preview__plate');
  const plate1000 = plate.getAttribute('data-plate-1000') || plate.src;
  const plate2000 = plate.getAttribute('data-plate-2000') || plate1000;
  if (spec.plate && !plate1000.includes(spec.plate)) {
    // A metafield may name a different plate: swap the file name inside the asset URL.
    const swap = (url) => url.replace(/lm-plate-[a-z]+-(\d+)\.jpg/, `${spec.plate}-$1.jpg`);
    plate.src = swap(plate1000);
    plate.srcset = `${swap(plate1000)} 1000w, ${swap(plate2000)} 2000w`;
  }
  const bub = box.querySelector('[data-bub]');
  const el = (k) => box.querySelector(`[data-el="${k}"]`);
  const dream = spec.layout === 'dream';
  const circus = spec.layout === 'circus';
  const officer = spec.layout === 'officer';
  const badge = spec.layout === 'badge';
  const firetruck = spec.layout === 'firetruck';
  const dino = spec.layout === 'dino';
  const tail = spec.layout === 'tail';
  const seahorse = spec.layout === 'seahorse';
  const shell = spec.layout === 'shell';
  const rose = spec.layout === 'rose';
  const ageOnly = spec.layout === 'age';
  const manege = spec.layout === 'manege';
  const initials = spec.layout === 'initials';
  const scoreboard = spec.layout === 'scoreboard';
  const pigskin = spec.layout === 'pigskin';
  const hoop = spec.layout === 'hoop';
  const cameo = spec.layout === 'cameo';
  const mirror = spec.layout === 'mirror';
  const [l, t, r, b] = spec.geom;
  Object.assign(bub.style, { left: `${l}%`, top: `${t}%`, width: `${r - l}%`, height: `${b - t}%` });
  box.querySelectorAll('.L1').forEach((e) => e.classList.toggle('is-hidden', dream || circus || officer || badge || firetruck || dino || tail || seahorse || shell || rose || ageOnly || manege || initials || scoreboard || pigskin || hoop || cameo || mirror));
  box.querySelectorAll('.L2').forEach((e) => e.classList.toggle('is-hidden', !dream));
  box.querySelectorAll('.L3').forEach((e) => e.classList.toggle('is-hidden', !circus));
  box.querySelectorAll('.L4').forEach((e) => e.classList.toggle('is-hidden', !officer));
  box.querySelectorAll('.L5').forEach((e) => e.classList.toggle('is-hidden', !badge));
  box.querySelectorAll('.L6').forEach((e) => e.classList.toggle('is-hidden', !firetruck));
  box.querySelectorAll('.L7').forEach((e) => e.classList.toggle('is-hidden', !dino));
  box.querySelectorAll('.L8').forEach((e) => e.classList.toggle('is-hidden', !tail));
  box.querySelectorAll('.L9').forEach((e) => e.classList.toggle('is-hidden', !seahorse));
  box.querySelectorAll('.L10').forEach((e) => e.classList.toggle('is-hidden', !shell));
  box.querySelectorAll('.L11').forEach((e) => e.classList.toggle('is-hidden', !rose));
  box.querySelectorAll('.L12').forEach((e) => e.classList.toggle('is-hidden', !ageOnly));
  box.querySelectorAll('.L13').forEach((e) => e.classList.toggle('is-hidden', !manege));
  box.querySelectorAll('.L14').forEach((e) => e.classList.toggle('is-hidden', !initials));
  box.querySelectorAll('.L15').forEach((e) => e.classList.toggle('is-hidden', !scoreboard));
  box.querySelectorAll('.L16').forEach((e) => e.classList.toggle('is-hidden', !pigskin));
  box.querySelectorAll('.L17').forEach((e) => e.classList.toggle('is-hidden', !hoop));
  box.querySelectorAll('.L18').forEach((e) => e.classList.toggle('is-hidden', !cameo));
  box.querySelectorAll('.L19').forEach((e) => e.classList.toggle('is-hidden', !mirror));
  // A plate that is not the usual 4:5 keeps its own shape rather than being cropped to fit.
  if (spec.plateAspect) box.style.aspectRatio = String(spec.plateAspect);

  // A name-only layout has no message line: hide those fields and stop requiring them.
  const messageFields = root.querySelector('[data-message-fields]');
  if (messageFields && spec.nameOnly) messageFields.hidden = true;
  // A piece that carries only a message: the name field has nothing to say on it.
  const nameField = root.querySelector('[data-name-field]');
  // A piece whose crest sits between two names takes them in two fields.
  const nameField2 = root.querySelector('[data-name2-field]');
  if (nameField2 && spec.name2) {
    nameField2.hidden = false;
    if (spec.name2Label) nameField2.querySelector('[data-name2-label]').textContent = spec.name2Label;
    if (spec.name2Hint) nameField2.querySelector('[data-name2-hint]').textContent = spec.name2Hint;
  }
  if (messageFields && spec.ageOnly) messageFields.hidden = true;
  if (messageFields && spec.markOnly) messageFields.hidden = true;
  if (messageFields && spec.cameoOnly) messageFields.hidden = true;
  if (nameField && (spec.messageOnly || spec.ageOnly || spec.markOnly || spec.cameoOnly)) {
    nameField.hidden = true;
    nameField.querySelector('input')?.removeAttribute('required');
  }

  // Some pieces take initials rather than a name; the field says so.
  if (spec.nameLabel) {
    const lab = root.querySelector('[data-name-field] .lm-field__label span');
    if (lab) lab.textContent = spec.nameLabel;
    const input = root.querySelector('[data-name]');
    if (input) {
      input.placeholder = spec.nameLabel;
      if (spec.nameMax) input.maxLength = spec.nameMax;
    }
  }
  const hint = root.querySelector('[data-name-hint]');
  if (hint && spec.nameHint) {
    hint.textContent = spec.nameHint;
  } else if (hint) {
    hint.textContent = seahorse
      ? 'First name only, please. It is set along the arc inside the bubble, in the collection\u2019s engraved script. Longer names are sized by the atelier to fit.'
      : tail
      ? 'A short line, please — it is set along the tail in the collection\u2019s hand-drawn lettering. Longer wording is sized by the atelier to fit.'
      : dino
      ? 'First name only, please. It is set in the collection\u2019s display capitals. Longer names are sized by the atelier to fit.'
      : firetruck
      ? 'First name only, please. It sits inside the badge beneath “Sound The Sirens”. Longer names are sized by the atelier to fit.'
      : officer
      ? 'First name only, please. It sits in the red line above the badge; the number goes inside the badge. Longer names are sized by the atelier to fit.'
      : badge
      ? 'First name only, please. It is set in bold capitals on the star. Longer names are sized by the atelier to fit.'
      : circus
      ? 'First name only, please. It sits beneath “Today’s Main Attraction” in the collection’s script. Longer names are sized by the atelier to fit.'
      : dream
      ? 'First name only, please. It appears as “Baby Name” in the collection’s capitals. Longer names are sized by the atelier to fit.'
      : 'First name only, please. It sits after Oh Baby and the crest, in the collection’s script. Longer names are sized by the atelier to fit.';
  }
  const sampleHouse = root.querySelector('[data-sample-house]');
  if (sampleHouse && dream) sampleHouse.classList.add('is-script');

  const px = (v) => `${v.toFixed(2)}px`;
  let last = { name: '', message: '', style: 'house', template: null, age: '' };

  // A positioned line: centred on cx (fraction of bw), sized by F (fraction of bw), sat on its
  // baseline (fraction of bh); shrinks as a whole if wider than maxW.
  function setLine(node, text, L, bw, bh, base) {
    node.style.fontFamily = L.font;
    node.style.fontWeight = String(L.weight || 400);
    // A foiled inscription: the sheen is a gradient clipped to the letterforms, with the flat
    // colour left underneath for anything that cannot clip a background to text.
    node.style.color = L.colour;
    if (L.stroke) {
      node.style.webkitTextStrokeWidth = `${(L.strokeWidth || 0.055) * L.F * bw}px`;
      node.style.webkitTextStrokeColor = L.stroke;
      node.style.paintOrder = 'stroke fill';
    } else {
      node.style.webkitTextStrokeWidth = '';
      node.style.webkitTextStrokeColor = '';
      node.style.paintOrder = '';
    }
    if (L.gradient) {
      node.style.backgroundImage = L.gradient;
      node.style.webkitBackgroundClip = 'text';
      node.style.backgroundClip = 'text';
      node.style.webkitTextFillColor = 'transparent';
    } else {
      node.style.backgroundImage = '';
      node.style.webkitBackgroundClip = '';
      node.style.backgroundClip = '';
      node.style.webkitTextFillColor = '';
    }
    node.style.letterSpacing = `${L.track || 0}em`;
    node.style.textTransform = L.upper ? 'uppercase' : 'none';
    // A small-caps cut sets the lowercase as capitals: the name keeps its own case so the
    // initials stay full height and the rest drops to small caps, as the printed piece does.
    node.style.fontVariantCaps = L.caps || 'normal';
    node.textContent = text;
    const F = L.F * bw;
    node.style.fontSize = px(F);
    node.style.width = 'auto';
    node.style.transform = '';
    const w = node.getBoundingClientRect().width;
    const maxW = (L.maxW || 0.8) * bw;
    const scale = w > maxW ? maxW / w : 1;
    // `align` anchors an edge instead of the centre, so a name can stop short of a crest
    const anchor = (L.cx ?? 0.5) * bw;
    const wide = w * scale;
    const left = L.align === 'right' ? anchor - wide : L.align === 'left' ? anchor : anchor - wide / 2;
    node.style.left = px(left);
    node.style.top = px(base * bh - L.baseOff * F * scale);
    node.style.transformOrigin = 'left top';
    const rot = L.rot ? ` rotate(${L.rot}deg)` : '';
    if (L.rot) node.style.transformOrigin = 'center center';
    node.style.transform = (scale < 1 ? `scale(${scale.toFixed(3)})` : '') + rot;
  }

  // A stack of centred lines: whatever the count, the block stays centred on `mid`, so a short
  // message sits where the long one's middle line does rather than drifting to the top.
  function setStack(keys, lines, L, bw, bh) {
    const n = lines.length;
    keys.forEach((k, i) => {
      const node = el(k);
      if (!node) return;
      if (i >= n) {
        node.textContent = '';
        return;
      }
      setLine(node, lines[i], L, bw, bh, L.mid + (i - (n - 1) / 2) * L.gap);
    });
  }

  // Free wording broken over at most `max` lines, each kept within `L.wrap` of the bubble width.
  function wrapLines(text, L, bw, max) {
    const words = String(text || '').split(/\s+/).filter(Boolean);
    if (!words.length) return [''];
    const limit = (L.wrap || 0.5) * bw;
    const probe = document.createElement('span');
    probe.style.cssText = `position:absolute;visibility:hidden;white-space:nowrap;line-height:1;font:${L.F * bw}px ${L.font}`;
    box.appendChild(probe);
    const width = (t) => {
      probe.textContent = t;
      return probe.getBoundingClientRect().width;
    };
    const lines = [];
    let cur = words[0];
    for (let i = 1; i < words.length; i++) {
      const next = `${cur} ${words[i]}`;
      if (lines.length < max - 1 && width(next) > limit) {
        lines.push(cur);
        cur = words[i];
      } else {
        cur = next;
      }
    }
    lines.push(cur);
    probe.remove();
    return lines;
  }

  function styleOf(k) {
    if (!dream) {
      return {
        house: { F: spec.msgF, track: spec.msgTrack, base: BASE.house },
        script: { F: 0.11, track: 0, base: BASE.script },
        serif: { F: 0.062, track: 0.02, base: BASE.serif },
        minimal: { F: 0.042, track: 0.24, base: BASE.minimal },
      }[k];
    }
    return {
      house: { F: spec.line1F, track: 0, base: BASE.script },
      script: { F: spec.line1F, track: 0, base: BASE.script },
      serif: { F: 0.06, track: 0.02, base: BASE.serif },
      minimal: { F: 0.04, track: 0.2, base: BASE.minimal },
    }[k];
  }

  function paint(state) {
    if (state) last = state;
    const { name, message, style } = last;
    const bw = bub.clientWidth;
    const bh = bub.clientHeight;
    if (!bw) return;

    if (tail) {
      const seg = last.template || parseTemplate(spec.phrases[0]);
      setLine(el('t1'), fillTemplate(seg, name || '', '')[0], spec.line, bw, bh, spec.line.base);
      return;
    }
    if (rose) {
      setLine(el('r1'), name || 'Name', spec.line, bw, bh, spec.line.base);
      return;
    }
    if (mirror) {
      // One arced line and nothing else, curved round the top of the glass.
      const seg = last.template || parseTemplate(spec.phrases[0]);
      const [l1] = fillTemplate(seg, name || 'Name', last.age || '#');
      setArc(el('mrc'), l1 || '', spec.arc, bw, bh);
      return;
    }
    if (cameo) {
      // Either the printed frame with a number in it, or the illustrated frame laid over the top.
      const portrait = last.cameoMode === 'Cinderella';
      const img = el('cfimg');
      if (img) {
        img.classList.toggle('is-hidden', !portrait);
        if (portrait) {
          const fw = spec.frame.width * bw;
          place(img, spec.frame.left * bw, spec.frame.top * bh, fw, fw / spec.frame.aspect);
        }
      }
      setLine(el('cnum'), portrait ? '' : last.age || '', spec.num, bw, bh, spec.num.base);
      return;
    }
    if (hoop) {
      // two arcs round the crest: the name above, the motto beneath
      const seg = last.template || parseTemplate(spec.phrases[0]);
      const [motto] = fillTemplate(seg, name || 'NAME', last.age || '');
      setArc(el('hp1'), (name || 'NAME').toUpperCase(), spec.arcName, bw, bh);
      setArc(el('hp2'), (motto || '').toUpperCase(), spec.arcMotto, bw, bh);
      return;
    }
    if (scoreboard) {
      // up to four stacked lines: the occasion, the name, a joining word, the number
      const seg = last.template || parseTemplate(spec.phrases[0]);
      let filled = fillTemplate(seg, name || 'NAME', last.age || '#');
      // Some pieces carry the number on its own line, ahead of the chosen wording.
      if (spec.ageFirst) filled = [last.age || '#', ...filled];
      if (spec.ageLast) filled = [...filled, last.age || '#'];
      spec.lines.forEach((L, i) => {
        const node = el(`sb${i + 1}`);
        if (!node) return;
        // Athletic lettering is set in capitals; a storybook hand keeps its own case.
        const text = spec.noUpper ? filled[i] || '' : (filled[i] || '').toUpperCase();
        setLine(node, text, L, bw, bh, L.base);
      });
      return;
    }
    if (pigskin) {
      // either a logo the customer uploads, or a short line in the collection's brush script
      setLine(el('pg1'), last.logoMode ? '' : last.custom || 'Your Text', spec.line, bw, bh, spec.line.base);
      return;
    }
    if (initials) {
      setLine(el('i1'), name || 'Ah', spec.line, bw, bh, spec.line.base);
      return;
    }
    if (manege) {
      const seg = last.template || parseTemplate(spec.phrases[0]);
      const [l2, l3] = fillTemplate(seg, name || 'Name', last.age || '#');
      if (spec.name2) {
        // two names, one either side of the crest printed on the balloon
        setLine(el('m1'), name || 'First', spec.name, bw, bh, spec.name.base);
        setLine(el('m1b'), last.name2 || 'Last', spec.name2, bw, bh, spec.name2.base);
        setLine(el('m2'), l2, spec.line2, bw, bh, spec.line2.base);
        setLine(el('m3'), l3, spec.line3, bw, bh, spec.line3.base);
        return;
      }
      el('m1b').textContent = '';
      setLine(el('m1'), name || 'Name', spec.name, bw, bh, spec.name.base);
      setLine(el('m2'), l2, spec.line2, bw, bh, spec.line2.base);
      setLine(el('m3'), l3, spec.line3, bw, bh, spec.line3.base);
      return;
    }
    if (ageOnly) {
      setLine(el('g1'), last.age || '0', spec.line, bw, bh, spec.line.base);
      return;
    }
    if (seahorse) {
      const seg = last.template || parseTemplate(spec.phrases[0]);
      const [l1, ...rest] = fillTemplate(seg, name || 'Name', last.age || '#');
      setArc(el('sarc'), l1, spec.arc, bw, bh);
      const given = rest.filter(Boolean);
      const lines = given.length > 1 ? given.slice(0, 3) : wrapLines(given[0] || '', spec.lines, bw, 3);
      setStack(['s2', 's3', 's4'], lines, spec.lines, bw, bh);
      return;
    }
    if (shell) {
      const seg = last.template || parseTemplate(spec.phrases[0]);
      const text = fillTemplate(seg, name || '', last.age || '').filter(Boolean).join(' ');
      setStack(['q1', 'q2', 'q3'], wrapLines(text, spec.lines, bw, 3), spec.lines, bw, bh);
      return;
    }
    if (dino) {
      const seg = last.template || parseTemplate(spec.phrases[0]);
      const [l1, mid, l3] = fillTemplate(seg, name || 'Name', last.age || '#');
      setArc(el('darc'), l1, spec.arc, bw, bh);
      setLine(el('dnum'), mid, spec.num, bw, bh, spec.num.base);
      // the line below the number runs to two lines, as in the print
      const words = l3.split(/\s+/).filter(Boolean);
      let a = l3;
      let b = '';
      if (words.length > 1) {
        let best = words.length - 1;
        let bestD = Infinity;
        for (let i = 1; i < words.length; i++) {
          const d = Math.abs(words.slice(0, i).join(' ').length - words.slice(i).join(' ').length);
          if (d < bestD) { bestD = d; best = i; }
        }
        a = words.slice(0, best).join(' ');
        b = words.slice(best).join(' ');
      }
      setLine(el('d3'), a, spec.line3, bw, bh, spec.line3.base);
      setLine(el('d4'), b, spec.line4, bw, bh, spec.line4.base);
      return;
    }
    if (firetruck) {
      const seg = last.template || parseTemplate(spec.phrases[0]);
      const [l1, l2] = fillTemplate(seg, name || 'Name', last.age || '#');
      setArc(el('arc'), l1, spec.arc, bw, bh);
      setLine(el('f2'), l2, spec.line2, bw, bh, spec.line2.base);
      return;
    }
    if (officer) {
      const seg = last.template || parseTemplate(spec.phrases[0]);
      const [l1, l2, st] = fillTemplate(seg, name || 'Name', last.age || (seg[2]?.includes('{age}') ? '#' : ''));
      setLine(el('o1'), l1, spec.line1, bw, bh, spec.line1.base);
      setLine(el('o2'), l2, spec.line2, bw, bh, spec.line2.base);
      setLine(el('oStar'), st, spec.star, bw, bh, spec.star.base);
      return;
    }
    if (badge) {
      const seg = last.template || parseTemplate(spec.phrases[0]);
      const [l1, l2] = fillTemplate(seg, name || 'Name', last.age);
      setLine(el('b1'), l1, spec.lines, bw, bh, spec.lines.bases[0]);
      setLine(el('b2'), l2, spec.lines, bw, bh, spec.lines.bases[1]);
      return;
    }
    if (circus) {
      const hd = el('cirqueHeader');
      if (spec.headerW) {
        const hw = spec.headerW * bw;
        Object.assign(hd.style, { left: px(spec.headerLeft * bw), top: px(spec.headerTop * bh), width: px(hw), height: px(hw * spec.headerAspect) });
      } else {
        hd.classList.add('is-hidden');
      }
      // the gold line above the name comes from the chosen phrase
      if (spec.line1) {
        const seg = last.template || parseTemplate(spec.phrases[0]);
        setLine(el('cline'), fillTemplate(seg, name || 'Name', '')[0], spec.line1, bw, bh, spec.line1.base);
      }
      // The name is sized so the printed sample ("Your Name") spans nameWidth of the bubble; a
      // longer name shrinks to the same width, a shorter one keeps that size.
      const cn = el('cname');
      cn.textContent = name;
      cn.style.color = spec.nameColour;
      const sample = name || 'Your Name';
      cn.style.fontSize = '100px';
      cn.style.width = 'auto';
      cn.style.left = '0';
      cn.style.transform = '';
      const probe = document.createElement('span');
      probe.style.cssText = `position:absolute;visibility:hidden;white-space:nowrap;font:100px ${getComputedStyle(cn).fontFamily}`;
      probe.textContent = sample;
      box.appendChild(probe);
      const w100 = probe.getBoundingClientRect().width || 1;
      probe.remove();
      const F = Math.min((spec.nameWidth * bw * 100) / w100, (spec.nameWidth * bw * 100) / (probeWidth('Your Name', getComputedStyle(cn).fontFamily, box) || w100));
      cn.style.fontSize = px(F);
      cn.style.width = px(bw);
      cn.style.top = px(spec.base2 * bh - BASE.cirque * F);
      return;
    }

    if (!dream) {
      const oh = el('oh');
      const cr = el('crest1');
      const nm = el('name');
      const ohW = spec.ohW * bw;
      const ohH = ohW * ASPECT.oh;
      const crH = spec.crestH * bh;
      const crW = crH / ASPECT.crest;
      const F = spec.nameF * bw;
      const base = spec.base1 * bh;
      nm.style.fontSize = px(F);
      nm.style.color = spec.nameColour;
      nm.textContent = name;
      nm.style.transform = '';
      const nmW = nm.getBoundingClientRect().width;
      const total = ohW + spec.gap1 * bw + crW + spec.gap2 * bw + nmW;
      const scale = Math.min(1, (0.88 * bw) / total);
      // The row normally centres on the plate. On a plate that already carries the crest printed,
      // the row has to centre on that crest instead, which is rarely the exact middle.
      const x0 = (spec.rowCx ?? 0.5) * bw - (total * scale) / 2;
      place(oh, x0, base - spec.ohBase * ohH * scale, ohW * scale, ohH * scale);
      place(cr, x0 + (ohW + spec.gap1 * bw) * scale, base - crH * scale, crW * scale, crH * scale);
      nm.style.left = px(x0 + (ohW + spec.gap1 * bw + crW + spec.gap2 * bw) * scale);
      nm.style.top = px(base - BASE.script * F * scale);
      nm.style.transform = scale < 1 ? `scale(${scale.toFixed(3)})` : '';

      const ms = el('msg');
      const st = styleOf(style);
      const MF = st.F * bw;
      ms.className = `lm-preview__msg L1 ${FAM[style]}`;
      ms.textContent = message;
      Object.assign(ms.style, {
        fontSize: px(MF),
        letterSpacing: `${st.track}em`,
        width: px(spec.wrap * bw),
        left: px(((1 - spec.wrap) / 2) * bw),
        top: px(spec.base2 * bh - st.base * MF - ((1.22 - 1) / 2) * MF),
      });
    } else {
      const row = el('row');
      const st = styleOf(style);
      const F = st.F * bw;
      const crH = spec.crestH * bw;
      const crW = crH / ASPECT.crest;
      const words = message.split(/\s+/).filter(Boolean);
      let best = 0;
      let bestD = Infinity;
      for (let i = 1; i < words.length; i++) {
        const d = Math.abs(words.slice(0, i).join(' ').length - words.slice(i).join(' ').length);
        if (d < bestD) {
          bestD = d;
          best = i;
        }
      }
      el('left').textContent = words.slice(0, best).join(' ');
      el('right').textContent = words.slice(best).join(' ');
      row.className = `lm-preview__row L2 ${style === 'house' ? 'f-script' : FAM[style]}`;
      Object.assign(row.style, { fontSize: px(F), letterSpacing: `${st.track}em`, left: '0', width: px(bw), justifyContent: 'center' });
      // The crest hangs from the text baseline, not the row's descender edge: its bottom sits
      // crestDrop of its own height below the baseline. With align-items:baseline the row's
      // baseline is st.base·F from the row top, so offset the crest from there.
      const cr = el('crest2');
      Object.assign(cr.style, {
        width: px(crW),
        height: px(crH),
        margin: `0 ${px(spec.gapR * bw)} 0 ${px(spec.gapL * bw)}`,
        alignSelf: 'flex-start',
        marginTop: px(st.base * F - crH + spec.crestDrop * crH),
      });
      row.style.transform = '';
      // As with the oh-baby row: a plate that carries the crest printed sets where the row sits.
      row.style.left = px(((spec.rowCx ?? 0.5) - 0.5) * bw);
      row.style.top = px(spec.base1 * bh - st.base * F);
      const contentW = [...row.children].reduce((s, e) => s + e.getBoundingClientRect().width, 0) + (spec.gapL + spec.gapR) * bw;
      if (contentW > 0.84 * bw) {
        row.style.transformOrigin = 'center top';
        row.style.transform = `scale(${((0.84 * bw) / contentW).toFixed(3)})`;
      }

      const bl = el('baby');
      const NF = spec.nameF * bw;
      el('name2').textContent = name || 'Name';
      Object.assign(bl.style, {
        fontSize: px(NF),
        letterSpacing: `${spec.nameTrack}em`,
        left: '0',
        width: px(bw),
        textAlign: 'center',
        top: px(spec.base2 * bh - BASE.bodoni * NF),
        transformOrigin: 'center top',
      });
      bl.style.transform = '';
      const blW = bl.scrollWidth;
      if (blW > 0.86 * bw) bl.style.transform = `scale(${((0.86 * bw) / blW).toFixed(3)})`;

      const td = el('teddy');
      const tw = spec.teddyW * bw;
      const th = tw * ASPECT.teddy;
      Object.assign(td.style, { width: px(tw), height: px(th), left: px((bw - tw) / 2), top: px(spec.teddyTop * bh) });
    }
  }

  function place(node, x, y, w, h) {
    node.style.left = px(x);
    node.style.top = px(y);
    node.style.width = px(w);
    node.style.height = px(h);
  }

  new ResizeObserver(() => paint()).observe(box);
  return { paint };
}

/* ==================================================================== Delivery calendar */
function createCalendar(root, cfg, onChange) {
  const toggle = root.querySelector('[data-date-toggle]');
  const text = root.querySelector('[data-date-text]');
  const cal = root.querySelector('[data-cal]');
  const grid = root.querySelector('[data-cal-grid]');
  const monthEl = root.querySelector('[data-cal-month]');
  const prev = root.querySelector('[data-cal-prev]');
  const next = root.querySelector('[data-cal-next]');
  const propDate = root.querySelector('[data-prop-date]');
  const propIso = root.querySelector('[data-prop-date-iso]');
  const api = { value: null, toggle, open, close };
  if (!toggle || !cal) return api;

  const DAY = 86400000;
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const allowedDays = new Set(
    String(cfg.deliveryDays || '')
      .split(',')
      .map((s) => s.trim().slice(0, 3).toLowerCase())
      .filter(Boolean)
  );
  const blackouts = parseBlackouts(cfg.blackouts);
  const today = startOfDay(new Date());
  const now = new Date();
  const cutoff = cfg.cutoffHour === '' || cfg.cutoffHour == null ? null : parseInt(cfg.cutoffHour, 10);
  let lead = Number(cfg.leadDays) || 0;
  if (cutoff != null && !Number.isNaN(cutoff) && now.getHours() >= cutoff) lead += 1;
  const first = new Date(today.getTime() + lead * DAY);
  const last = new Date(today.getTime() + (Number(cfg.horizonDays) || 90) * DAY);
  let view = new Date(first.getFullYear(), first.getMonth(), 1);
  let selected = null;

  function startOfDay(d) {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }
  function iso(d) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }
  function parseBlackouts(raw) {
    const out = [];
    for (const line of String(raw || '').split(/\r?\n/)) {
      const dates = line.match(/\d{4}-\d{2}-\d{2}/g);
      if (!dates) continue;
      const a = new Date(dates[0] + 'T00:00:00');
      const z = dates[1] ? new Date(dates[1] + 'T00:00:00') : a;
      out.push([Math.min(a, z), Math.max(a, z)]);
    }
    return out;
  }
  function allowed(d) {
    if (d < first || d > last) return false;
    if (!allowedDays.has(dayNames[d.getDay()].toLowerCase())) return false;
    const ts = d.getTime();
    return !blackouts.some(([a, z]) => ts >= a && ts <= z);
  }
  function firstAllowed() {
    for (let d = new Date(first); d <= last; d = new Date(d.getTime() + DAY)) if (allowed(d)) return d;
    return null;
  }
  const firstOk = firstAllowed();

  function render(focusDate) {
    monthEl.textContent = view.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    prev.disabled = view <= new Date(first.getFullYear(), first.getMonth(), 1);
    next.disabled = view >= new Date(last.getFullYear(), last.getMonth(), 1);
    grid.innerHTML = '';
    const lead = view.getDay();
    for (let i = 0; i < lead; i++) grid.appendChild(document.createElement('span'));
    const daysInMonth = new Date(view.getFullYear(), view.getMonth() + 1, 0).getDate();
    for (let n = 1; n <= daysInMonth; n++) {
      const d = new Date(view.getFullYear(), view.getMonth(), n);
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'lm-cal__day';
      btn.textContent = String(n);
      btn.dataset.iso = iso(d);
      const ok = allowed(d);
      if (!ok) {
        btn.classList.add('is-blocked');
        btn.setAttribute('aria-disabled', 'true');
        btn.tabIndex = -1;
      }
      if (d.getTime() === today.getTime()) btn.classList.add('is-today');
      if (firstOk && d.getTime() === firstOk.getTime()) btn.classList.add('is-first');
      if (selected && d.getTime() === selected.getTime()) {
        btn.classList.add('is-selected');
        btn.setAttribute('aria-pressed', 'true');
      }
      btn.setAttribute('aria-label', d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) + (ok ? '' : ' (unavailable)'));
      btn.tabIndex = ok && (focusDate ? d.getTime() === focusDate.getTime() : false) ? 0 : btn.tabIndex;
      grid.appendChild(btn);
    }
    // Roving tabindex: one focusable day (the selected, the first available in view, or the first allowed).
    if (!grid.querySelector('.lm-cal__day[tabindex="0"]')) {
      const target = grid.querySelector('.lm-cal__day.is-selected') || grid.querySelector('.lm-cal__day:not(.is-blocked)');
      if (target) target.tabIndex = 0;
    }
    if (focusDate) grid.querySelector(`[data-iso="${iso(focusDate)}"]`)?.focus();
  }

  function select(d) {
    selected = d;
    api.value = d;
    text.textContent = d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
    propDate.value = text.textContent;
    propIso.value = iso(d);
    toggle.classList.add('is-set');
    close();
    onChange();
  }

  function open() {
    if (!cal.hidden) return;
    view = new Date((selected || firstOk || first).getFullYear(), (selected || firstOk || first).getMonth(), 1);
    cal.hidden = false;
    toggle.setAttribute('aria-expanded', 'true');
    render(selected || firstOk);
  }
  function close() {
    cal.hidden = true;
    toggle.setAttribute('aria-expanded', 'false');
  }

  toggle.addEventListener('click', () => (cal.hidden ? open() : close()));
  prev.addEventListener('click', () => {
    view = new Date(view.getFullYear(), view.getMonth() - 1, 1);
    render();
  });
  next.addEventListener('click', () => {
    view = new Date(view.getFullYear(), view.getMonth() + 1, 1);
    render();
  });
  grid.addEventListener('click', (e) => {
    const btn = e.target.closest('.lm-cal__day');
    if (!btn || btn.classList.contains('is-blocked')) return;
    select(new Date(btn.dataset.iso + 'T00:00:00'));
  });
  cal.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
      toggle.focus();
      return;
    }
    const btn = e.target.closest('.lm-cal__day');
    if (!btn) return;
    const delta = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 }[e.key];
    if (delta == null) return;
    e.preventDefault();
    let d = new Date(btn.dataset.iso + 'T00:00:00');
    for (let i = 0; i < 62; i++) {
      d = new Date(d.getTime() + delta * DAY);
      if (d < first || d > last) return;
      if (allowed(d)) break;
    }
    if (!allowed(d)) return;
    if (d.getMonth() !== view.getMonth() || d.getFullYear() !== view.getFullYear()) {
      view = new Date(d.getFullYear(), d.getMonth(), 1);
    }
    render(d);
  });
  document.addEventListener('click', (e) => {
    if (cal.hidden) return;
    if (!cal.contains(e.target) && !toggle.contains(e.target)) close();
  });

  return api;
}

/* ==================================================================== ZIP check */
function createZipCheck(root, cfg, onChange) {
  const input = root.querySelector('[data-zip]');
  const button = root.querySelector('[data-zip-check]');
  const status = root.querySelector('[data-zip-status]');
  const prop = root.querySelector('[data-prop-zip]');
  const api = { ok: false, input };
  if (!input) return api;

  const fees = new Map();
  for (const line of String(cfg.zips || '').split(/\r?\n/)) {
    const m = line.match(/(\d{5})\s*(?:\|\s*(\d+(?:\.\d+)?))?/);
    if (m) fees.set(m[1], m[2] != null ? Number(m[2]) : null);
  }
  const idle = status.textContent;

  function check(silent) {
    const z = input.value.trim();
    if (!/^\d{5}$/.test(z)) {
      if (!silent) setStatus('Please enter a five-digit ZIP code.', 'no');
      api.ok = false;
      prop.value = '';
      onChange();
      return;
    }
    if (fees.has(z)) {
      const fee = fees.get(z) ?? cfg.fee;
      setStatus(`We deliver to ${z}. Flat delivery fee $${fee}.`, 'ok');
      api.ok = true;
      prop.value = z;
      try {
        localStorage.setItem('lm_zip', z);
      } catch (_) {}
    } else {
      status.innerHTML = `We don’t reach ${z} yet. <a href="${cfg.contactUrl}">Write to us</a> and we’ll see what we can do.`;
      status.classList.remove('is-ok');
      status.classList.add('is-no');
      api.ok = false;
      prop.value = '';
    }
    onChange();
  }
  function setStatus(msg, kind) {
    status.textContent = msg;
    status.classList.toggle('is-ok', kind === 'ok');
    status.classList.toggle('is-no', kind === 'no');
  }

  button.addEventListener('click', () => check(false));
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      check(false);
    }
  });
  input.addEventListener('input', () => {
    input.value = input.value.replace(/\D/g, '').slice(0, 5);
    if (api.ok) {
      api.ok = false;
      prop.value = '';
      setStatus(idle, '');
      onChange();
    }
    if (input.value.length === 5) check(true);
  });

  try {
    const saved = localStorage.getItem('lm_zip');
    if (saved && /^\d{5}$/.test(saved)) {
      input.value = saved;
      check(true);
    }
  } catch (_) {}

  return api;
}

const root = document.querySelector('[data-lm-product]');
if (root) init(root);
